/*
 * Copyright (c) 2022 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Tuesday, March 22nd 2022, 7:29:05 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import React from "react";
import { useDebounceFn, useDidMount } from "lib/utils";
import { useWatch, UseFormReturn, useFormState } from "react-hook-form";
import { isEmpty } from "lodash";
import { useDeepEffect } from "lib/deep_hooks";

/***
 * A provider that providers takes in a save function and provides a debounced autosave function for use
 * by child components whether they are vanilla input elements or complex components.
 *
 */
interface IAutoSaveFormContext {
  autoSaveFormFn: () => void;
}

const AutoSaveFormContext = React.createContext<IAutoSaveFormContext>({ autoSaveFormFn: null });

export const useAutoSaveForm = () => React.useContext<IAutoSaveFormContext>(AutoSaveFormContext);

export interface AutoSaveFormProviderProps<TFormData> {
  submitFunction: () => void;
  timeout?: number;
  doDebounce?: boolean;
  children: React.ReactNode;
  useFormReturn: UseFormReturn<TFormData>;
  isNewData: boolean;
}

export const AutoSaveFormProvider = <TFormData,>({
  submitFunction,
  timeout = 500,
  doDebounce,
  children,
  useFormReturn,
  isNewData,
}: AutoSaveFormProviderProps<TFormData>) => {
  // Using an incremental number to trigger the debounce. We cannot use any passed html value because different input elements return different properties in the event object e.g. checkbox sets it in event.target.checked, text box in event.target.value etc.
  // TODO: Think of a better way!
  const [autoSaveCount, setAutoSaveCounter] = React.useState<number>(0);

  useDebounceFn(submitFunction, { timeout, doDebounce, deps: [autoSaveCount] }); // Debouncing the autosave function

  const autoSaveFormFn = () => setAutoSaveCounter((prev) => prev + 1);

  const { control } = useFormReturn;

  const didMount = useDidMount();

  /** Using useWacth because it is much more performant than the watch function returned by useForm
   * Reference documentation:
   * useWatch - https://react-hook-form.com/api/usewatch
   * watch from useForm - https://react-hook-form.com/api/useform/watch
   *
   */

  const allWatchedValues = useWatch({ control });

  // Ensuring we always subscribe to the formState by destructuring the value before using it. formState uses
  // a javascript Proxy and as per react hook form documentation, this is how formState should be read.
  // What the docs say:
  // Returned formState is wrapped with Proxy to improve render performance and skip extra computation if
  // specific state is not subscribed, so make sure you deconstruct or read it before render in order to
  // enable the subscription.
  // https://react-hook-form.com/api/useformstate/
  const { submitCount, isDirty } = useFormState({ control });

  // submitCount is reset to 0 whenever our form is reset so this is cool - https://react-hook-form.com/api/useformstate
  // only autosave if the data is new, i.e. id is undefined or if input values are dirty
  const doAutoSave = !isEmpty(allWatchedValues) && ((isNewData && submitCount === 0) || (didMount && isDirty));

  // Watch the fields for the form and autosave as they change. This is better than using the bubbled up onChange event because you can also listen to changes
  // on fields with complex components and subcomponents
  useDeepEffect(() => {
    if (doAutoSave) autoSaveFormFn();
  }, [allWatchedValues, doAutoSave]);

  return <AutoSaveFormContext.Provider value={{ autoSaveFormFn }}>{children}</AutoSaveFormContext.Provider>;
};
